import React, { AriaAttributes, ReactNode, useEffect, useMemo, useState } from "react"

import styled, { FlattenSimpleInterpolation, css } from "styled-components"

import { colors, spacing } from "@izettle/otto"
import { TextFieldMasked } from "@izettle/react"

import { theme } from "@/theme"
import { isPlatformIOS } from "@/util"

import { ErrorsList, FieldErrors } from "./field/field-errors"
import { getErrorListId, getDescriptionId } from "./field/helpers"

const REGEX_ALL_CHARACTERS_EXCEPT_NEW_LINE = /./
const REGEX_ONLY_ALPHABET_LETTERS = /[A-Za-z]/
const REGEX_ONLY_NUMBERS = /[0-9]/
const REGEX_PLUS_OR_DIGIT = /[+0-9]/
const REGEX_EITHER_A_OR_DASH = /[Aa-]/
const REGEX_EITHER_LETTER_OR_NUMBER = /[A-Za-z0-9]/
const REGEX_X_Y_Z_OR_NUMBER = /[xXyYzZ0-9]/

type TextFieldProps = {
  name: string
  value: string
  label: string | undefined
  placeholder?: string | undefined
  errors?: ErrorsList
  disabled?: boolean
  autofocus?: boolean
  mask?: string | undefined
  help?: ReactNode
  onChange: (value: string) => void
}

const Field = styled.div`
  margin-bottom: ${spacing.small};
  scroll-margin: ${spacing.xxLarge};

  @media ${theme.devices.desktopWide} {
    scroll-margin: ${spacing.medium};
  }
`

const Description = styled.div`
  margin: ${spacing.xxSmall} ${spacing.small} ${spacing.none} ${spacing.small};
`

const StyledContainer = styled.div<{ error: boolean; focus: boolean }>`
  .otto-text-field-outer-container {
    border-color: ${colors.selector.initial.default};
    &:focus {
      border-color: ${colors.selector.selected.active} !important;
    }
    ${({ error, focus }): FlattenSimpleInterpolation | false => {
      if (error && focus) {
        return css`
          border-color: ${colors.border.error};
        `;
      }
      if (!error && focus) {
        return css`
          border-color: ${colors.selector.selected.active};
        `;
      }
      if (!error && !focus) {
        return css`
          &:hover {
            border-color: ${colors.selector.initial.hover};
          }
        `;
      }
      return css``;
    }};
  }
`;

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      name,
      value,
      label,
      placeholder,
      errors = [],
      disabled = false,
      autofocus = false,
      mask,
      help,
      onChange,
    },
    ref,
  ) => {
    const id = `field_${name}`
    const [onFocus, setOnFocus] = useState(false);

    // - autofocus is not enabled for fields which already have value
    // - autofocus is not enabled on iOS, since we can't always reliably show keyboard.
    //     (keyboard is shown only on direct user interaction which is not the case when
    //     we are making step validation requests, for example)
    const autoFocusEnabled: boolean = autofocus && !value && !isPlatformIOS

    const hasError: boolean = useMemo(() => errors.length > 0, [errors])

    const ariaOptionalProps: AriaAttributes = {}
    const ariaDescribedBy: string[] = []

    if (errors.length > 0) ariaDescribedBy.push(getErrorListId(id))
    if (help) ariaDescribedBy.push(getDescriptionId(id))

    if (ariaDescribedBy.length > 0) {
      ariaOptionalProps["aria-describedby"] = ariaDescribedBy.join(" ")
    }

    useEffect(() => {
      if ((hasError || autoFocusEnabled) && !onFocus) {
        setOnFocus(true);
      }
    }, [hasError, autoFocusEnabled]);

    return (
      <Field data-property-name={name}>
        <StyledContainer error={hasError} focus={onFocus}>
        <TextFieldMasked
          ref={ref}
          id={id}
          value={value || ""}
          onAccept={(event): void => {
            onChange(event.value === "" ? "" : event.value)
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocusEnabled}
          maskOptions={{
            mask: (mask as string) ?? /.*/,
            definitions: {
              "*": REGEX_ALL_CHARACTERS_EXCEPT_NEW_LINE,
              "+": REGEX_PLUS_OR_DIGIT,
              "9": REGEX_ONLY_NUMBERS,
              a: REGEX_ONLY_ALPHABET_LETTERS,
              Ɐ: REGEX_EITHER_A_OR_DASH,
              Œ: REGEX_EITHER_LETTER_OR_NUMBER,
              "¿": REGEX_X_Y_Z_OR_NUMBER,
            },
          }}
          label={label ? label.toString() : undefined}
          aria-invalid={hasError}
          isErrorStateOverriden={hasError}
          onClick={() => {
            setOnFocus(true);
          }}
          onBlur={() => {
            setOnFocus(false);
          }}
          {...ariaOptionalProps}
        />
        </StyledContainer>
        <FieldErrors id={id} errorKeys={errors} />
        {help && <Description id={getDescriptionId(id)}>{help}</Description>}
      </Field>
    )
  },
)
